import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Input, List, ListItem, Text } from '@chakra-ui/react';
import { usePortal } from '../../infrastructure/portal/usePortal';
import type { I18nField } from '../../../domains/search/application/IESDocument';
import './SearchBarAutocomplete.scss';

export type SearchSuggestion = {
  id: string | number;
  title: I18nField;
  subTitle: I18nField;
  category: I18nField;
  description?: I18nField;
  link?: string;
};

function esLang(appLang: string): 'en' | 'he' {
  return appLang?.toLowerCase().startsWith('en') ? 'en' : 'he';
}

type SearchBarAutocompleteProps = {
  placeholder?: string;
  width?: number;
  height?: number;
  isClearable?: boolean;
  onSearch: (value: string) => void;
  onChange?: (value: string) => void;
  onSuggestionsFetch?: (query: string) => Promise<SearchSuggestion[]>;
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
  defaultValue?: string;
  value?: string;
  className?: string;
  autoFocus?: boolean;
  debounceDelay?: number;
  minCharsForSuggestions?: number;
  maxSuggestions?: number;
  isCategory?: boolean;
  disabled?: boolean;
  clearOnSelect?: boolean;
};

export function SearchBarAutocomplete({
  placeholder = 'header.search.placeholder',
  width,
  height,
  isClearable = true,
  onSearch,
  onChange,
  onSuggestionsFetch,
  onSuggestionSelect,
  defaultValue = '',
  value,
  className = '',
  autoFocus = false,
  debounceDelay = 300,
  minCharsForSuggestions = 2,
  maxSuggestions = 8,
  isCategory = true,
  disabled = false,
  clearOnSelect = false,
}: SearchBarAutocompleteProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = esLang(i18n.language);
  const [inputValue, setInputValue] = useState(
    value !== undefined ? value : defaultValue,
  );
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const justSelectedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalRoot = usePortal('dropdown');
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedValue(inputValue),
      debounceDelay,
    );
    return () => window.clearTimeout(timer);
  }, [inputValue, debounceDelay]);

  useEffect(() => {
    if (value !== undefined) setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
      setSelectedIndex(-1);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (disabled || !hasUserInteracted) return;
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (
      debouncedValue.trim().length < minCharsForSuggestions ||
      !onSuggestionsFetch
    ) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    onSuggestionsFetch(debouncedValue)
      .then((results) => {
        if (cancelled) return;
        const next = results.slice(0, maxSuggestions);
        setSuggestions(next);
        setIsOpen(next.length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setSuggestions([]);
        setIsOpen(false);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    debouncedValue,
    disabled,
    hasUserInteracted,
    maxSuggestions,
    minCharsForSuggestions,
    onSuggestionsFetch,
  ]);

  useEffect(() => {
    if (!isOpen || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, [isOpen]);

  const handleSearch = () => {
    if (!inputValue.trim()) return;
    onSearch(inputValue);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    justSelectedRef.current = true;
    if (onSuggestionSelect) {
      if (clearOnSelect) setInputValue('');
      setIsOpen(false);
      setSelectedIndex(-1);
      onSuggestionSelect(suggestion);
      return;
    }
    if (suggestion.link && suggestion.link !== '#') {
      navigate(suggestion.link);
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }
    if (clearOnSelect) setInputValue('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onSearch(suggestion.title[lang]);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
    onChange?.('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (event.key === 'Enter') handleSearch();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
        return;
      }
      handleSearch();
      return;
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  };

  const boxWidth = width || 400;
  const boxHeight = height || 44;

  return (
    <div
      ref={wrapperRef}
      className={`search-bar-autocomplete ${className}`.trim()}
      style={{ width: `${boxWidth}px`, height: `${boxHeight}px` }}
    >
      <div
        className="search-bar-autocomplete__input-wrapper"
        style={{ height: `${boxHeight}px` }}
      >
        <Input
          ref={inputRef}
          type="text"
          placeholder={t(placeholder)}
          value={inputValue}
          onChange={(event) => {
            const next = event.target.value;
            setInputValue(next);
            setSelectedIndex(-1);
            setHasUserInteracted(true);
            onChange?.(next);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setHasUserInteracted(true)}
          autoFocus={autoFocus}
          disabled={disabled}
          className="search-bar-autocomplete__input"
          height={`${boxHeight - 8}px`}
          dir="ltr"
          lang="en"
          style={{
            direction: 'ltr',
            unicodeBidi: 'plaintext',
            textAlign: 'left',
          }}
        />
        <div className="search-bar-autocomplete__buttons">
          <Button
            type="button"
            className="btn-search"
            onClick={handleSearch}
            aria-label="Search"
            isDisabled={disabled}
          >
            <i className="icon icon-map relative" />
          </Button>
          {isClearable && inputValue && !disabled ? (
            <Button
              type="button"
              className="btn-clear"
              onClick={handleClear}
              aria-label="Clear"
            >
              <i className="icon icon-trash-red relative" />
            </Button>
          ) : null}
        </div>
      </div>
      {isOpen && suggestions.length > 0 && !disabled && portalRoot
        ? createPortal(
            <Box
              ref={dropdownRef}
              className="search-bar-autocomplete__dropdown"
              dir="ltr"
              style={{
                direction: 'ltr',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
            >
              <List>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={`${suggestion.id}-${index}`}
                    className={`search-bar-autocomplete__suggestion ${
                      index === selectedIndex ? 'selected' : ''
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="suggestion-content">
                      <Text className="suggestion-title">
                        {suggestion.title[lang]}
                      </Text>
                      {suggestion.subTitle?.[lang] ? (
                        <Text className="suggestion-description">
                          {suggestion.subTitle[lang]}
                        </Text>
                      ) : null}
                    </div>
                    {isCategory && suggestion.category?.[lang] ? (
                      <span className="suggestion-type">
                        {suggestion.category[lang]}
                      </span>
                    ) : null}
                  </ListItem>
                ))}
              </List>
              {isLoading ? (
                <div className="search-bar-autocomplete__loading">
                  {t('header.search.loading')}
                </div>
              ) : null}
            </Box>,
            portalRoot,
          )
        : null}
    </div>
  );
}
